import { useState, useEffect, useRef } from "react";
import axios from "axios";

const DIAS_OPS = [
    { key: 15, label: "15 días" },
    { key: 30, label: "30 días" },
];

const SERIES = [
    { key: "neto",     label: "Neto vendedor",   color: "var(--verde-esmeralda)" },
    { key: "comision", label: "Comisión Zervicy", color: "var(--azul-logo-claro)" },
    { key: "pasarela", label: "Costo pasarela",   color: "var(--ambar-600)" },
    { key: "urgencia", label: "Urgencias",        color: "var(--rojo-600)" },
];

function fmtFecha(iso) {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    const meses = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
    return `${Number(d)} ${meses[Number(m) - 1]}`;
}

function fmtFechaCompleta(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    const meses = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
    return `${Number(d)} de ${meses[Number(m) - 1]} de ${y}`;
}

function fmtPeso(n) {
    if (!n && n !== 0) return "—";
    return "$" + new Intl.NumberFormat("es-CO").format(Math.round(n));
}

function fmtPesoShort(n) {
    if (!n) return "$0";
    if (n >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (n >= 1_000)     return "$" + (n / 1_000).toFixed(0) + "k";
    return "$" + Math.round(n);
}

// ── Gráfica de barras apiladas SVG ────────────────────────────────────────────
function StackedBarChart({ serie, dias }) {
    const svgRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);

    const W = 1000, H = 260, PAD_L = 64, PAD_R = 12, PAD_T = 16, PAD_B = 36;
    const AREA_W = W - PAD_L - PAD_R;
    const AREA_H = H - PAD_T - PAD_B;

    const maxTotal  = Math.max(...serie.map(d => SERIES.reduce((s, k) => s + (d[k.key] || 0), 0)), 1);
    const gridLines = 4;
    const barWidth  = AREA_W / serie.length;
    const barPad    = barWidth * 0.2;
    const barActual = barWidth - barPad * 2;
    const labelCada = dias <= 15 ? 3 : 5;
    const GAP       = 1;

    function xBar(i) { return PAD_L + i * barWidth + barPad; }

    const handleMouseMove = (e) => {
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const relX = (e.clientX - rect.left) * (W / rect.width) - PAD_L;
        const idx  = Math.floor(relX / barWidth);
        if (idx < 0 || idx >= serie.length) { setTooltip(null); return; }
        setTooltip({ svgX: xBar(idx) + barActual / 2, idx });
    };

    return (
        <div style={{ position: "relative" }}>
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
                style={{ width: "100%", height: "auto", display: "block", userSelect: "none" }}
                onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>

                {Array.from({ length: gridLines + 1 }, (_, i) => {
                    const y   = PAD_T + (i / gridLines) * AREA_H;
                    const val = maxTotal * (1 - i / gridLines);
                    return (
                        <g key={i}>
                            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="var(--color-track-bg)" strokeWidth="1" />
                            {val > 0 && (
                                <text x={PAD_L - 6} y={y + 4} textAnchor="end" fontSize="11" fill="var(--color-disabled-text)" fontFamily="system-ui,sans-serif">
                                    {fmtPesoShort(val)}
                                </text>
                            )}
                        </g>
                    );
                })}

                {serie.map((d, i) => {
                    const sel      = tooltip?.idx === i;
                    const dayTotal = SERIES.reduce((s, k) => s + (d[k.key] || 0), 0);
                    let cumPx = 0;
                    return (
                        <g key={d.fecha} opacity={tooltip && !sel ? 0.45 : 1} style={{ transition: "opacity 0.1s" }}>
                            {SERIES.map(({ key, color }) => {
                                const val = d[key] || 0;
                                if (val === 0) return null;
                                const hPx = (val / maxTotal) * AREA_H;
                                const y   = PAD_T + AREA_H - cumPx - hPx;
                                cumPx    += hPx + GAP;
                                const isTop = key === [...SERIES].reverse().find(s => (d[s.key] || 0) > 0)?.key;
                                return (
                                    <rect key={key}
                                        x={xBar(i)} y={y}
                                        width={barActual} height={Math.max(hPx - GAP, 1)}
                                        rx={isTop ? 3 : 0} ry={isTop ? 3 : 0}
                                        fill={color} />
                                );
                            })}
                            {dayTotal > 0 && (
                                <rect x={xBar(i)} y={PAD_T + AREA_H - (dayTotal / maxTotal) * AREA_H}
                                    width={barActual} height={(dayTotal / maxTotal) * AREA_H}
                                    fill="transparent"
                                    stroke={sel ? "var(--color-track-bg)" : "none"} strokeWidth="1.5" rx="3" ry="3" />
                            )}
                        </g>
                    );
                })}

                {serie.map((d, i) => {
                    if (i % labelCada !== 0 && i !== serie.length - 1) return null;
                    return (
                        <text key={`lx-${i}`} x={xBar(i) + barActual / 2} y={H - 6}
                            textAnchor="middle" fontSize="10.5" fill="var(--color-disabled-text)" fontFamily="system-ui,sans-serif">
                            {fmtFecha(d.fecha)}
                        </text>
                    );
                })}

                {tooltip && (
                    <line x1={tooltip.svgX} y1={PAD_T} x2={tooltip.svgX} y2={PAD_T + AREA_H}
                        stroke="var(--color-track-bg)" strokeWidth="1" strokeDasharray="4 3" />
                )}
            </svg>

            {tooltip && (() => {
                const d = serie[tooltip.idx];
                if (!d) return null;
                const total = SERIES.reduce((s, k) => s + (d[k.key] || 0), 0);
                return (
                    <div style={{
                        position: "absolute", top: 8,
                        left: `calc(${(tooltip.svgX / W) * 100}% + 8px)`,
                        transform: tooltip.svgX > W * 0.65 ? "translateX(calc(-100% - 16px))" : "none",
                        background: "var(--gris-900)", color: "var(--blanco)", borderRadius: 10,
                        padding: "8px 14px", fontSize: 12, pointerEvents: "none",
                        whiteSpace: "nowrap", zIndex: 10, boxShadow: "0 4px 16px rgba(var(--negro-rgb), 0.22)",
                        minWidth: 200,
                    }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>{fmtFechaCompleta(d.fecha)}</div>
                        <div style={{ marginBottom: 4, color: "rgba(var(--blanco-rgb), 0.5)", fontSize: 11 }}>
                            {d.count || 0} pago{d.count !== 1 ? "s" : ""}
                        </div>
                        {SERIES.map(({ key, label, color }) => {
                            const v = d[key] || 0;
                            if (!v) return null;
                            return (
                                <div key={key} className="d-flex justify-content-between gap-3" style={{ marginBottom: 3 }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }}></div>
                                        <span style={{ color: "rgba(var(--blanco-rgb), 0.7)" }}>{label}</span>
                                    </div>
                                    <span style={{ fontWeight: 700, color }}>{fmtPeso(v)}</span>
                                </div>
                            );
                        })}
                        <div style={{ borderTop: "1px solid rgba(var(--blanco-rgb), 0.12)", marginTop: 6, paddingTop: 5 }}>
                            <div className="d-flex justify-content-between">
                                <span style={{ color: "rgba(var(--blanco-rgb), 0.55)", fontSize: 11 }}>Total cobrado</span>
                                <span style={{ fontWeight: 700 }}>{fmtPeso(total)}</span>
                            </div>
                            {d.descuento > 0 && (
                                <div className="d-flex justify-content-between mt-1">
                                    <span style={{ color: "var(--verde-esmeralda)", fontSize: 11 }}>
                                        <i className="bi bi-gift-fill me-1"></i>Descuentos 1ra compra
                                    </span>
                                    <span style={{ fontWeight: 700, color: "var(--verde-esmeralda)" }}>{fmtPeso(d.descuento)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({ label, valor, color, icono, sub }) {
    return (
        <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
                <div className="mb-3">
                    <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: `${color}18`, flexShrink: 0 }}>
                        <i className={`bi ${icono} fs-5`} style={{ color }}></i>
                    </div>
                </div>
                <p className="text-secondary fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>{label}</p>
                <h3 className="fw-bold text-dark mb-1">{valor}</h3>
                {sub && <p className="text-secondary mb-0" style={{ fontSize: "0.82rem" }}>{sub}</p>}
            </div>
        </div>
    );
}

// ── Barra de progreso horizontal ──────────────────────────────────────────────
function FilaBarra({ label, total, pct, color, sub }) {
    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
                <div>
                    <span style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)", fontWeight: 500 }}>{label}</span>
                    {sub && <div style={{ fontSize: "0.72rem", color: "var(--gris-400)" }}>{sub}</div>}
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <span style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)" }}>{fmtPeso(total)}</span>
                    <span style={{ fontSize: "0.8rem", fontWeight: 700, color, minWidth: 36, textAlign: "right" }}>{pct}%</span>
                </div>
            </div>
            <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 7, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
            </div>
        </div>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────
function ReportesPagos() {
    const [dias, setDias]         = useState(30);
    const [datos, setDatos]       = useState(null);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setCargando(true);
                const res = await axios.post("/reportes-pagos", { params: { dias } });
                if (res.data.exito) setDatos(res.data);
            } catch { /* silencioso */ } finally { setCargando(false); }
        })();
    }, [dias]);

    const serie   = datos?.serie   || [];
    const totales = datos?.totales || {};
    const metodos = datos?.metodos || [];

    const totalMonto = totales.montoTotal || 0;

    return (
        <div className="container-fluid pb-5">
            {/* Cabecera */}
            <div className="d-flex justify-content-between align-items-start pt-3 pb-2 mb-4 border-bottom flex-wrap gap-3">
                <div>
                    <h1 className="h4 fw-bold text-dark mb-0">Reportes de pagos</h1>
                    <p className="text-muted small mb-0 mt-1">Ingresos y composición del dinero recibido en la plataforma.</p>
                </div>
                <div className="d-flex gap-2">
                    {DIAS_OPS.map(op => (
                        <button
                            key={op.key}
                            className={`btn btn-sm rounded-3 fw-semibold ${dias === op.key ? "btn-dark" : "btn-outline-secondary"}`}
                            onClick={() => setDias(op.key)}
                        >
                            {op.label}
                        </button>
                    ))}
                </div>
            </div>

            {cargando ? (
                <div className="text-center py-5 text-muted">
                    <span className="spinner-border spinner-border-sm me-2"></span>Cargando...
                </div>
            ) : (
                <>
                    {/* ── Tiles resumen ── */}
                    <div className="row g-3 mb-4">
                        <div className="col-6 col-md-4 col-lg-3" style={{ minWidth: 0 }}>
                            <StatTile
                                label="Total recaudado"
                                valor={fmtPeso(totales.montoTotal)}
                                color="var(--gris-900)"
                                icono="bi-cash-stack"
                                sub={`${totales.count || 0} pagos`}
                            />
                        </div>
                        <div className="col-6 col-md-4 col-lg-3" style={{ minWidth: 0 }}>
                            <StatTile
                                label="Comisión Zervicy (bruta)"
                                valor={fmtPeso(totales.comision)}
                                color="var(--azul-logo-claro)"
                                icono="bi-percent"
                                sub={totalMonto > 0 ? `${Math.round((totales.comision / totalMonto) * 100)}% del total` : null}
                            />
                        </div>
                        <div className="col-6 col-md-4 col-lg-3" style={{ minWidth: 0 }}>
                            <StatTile
                                label="Neto a vendedores"
                                valor={fmtPeso(totales.neto)}
                                color="var(--verde-esmeralda)"
                                icono="bi-person-check-fill"
                                sub={totalMonto > 0 ? `${Math.round((totales.neto / totalMonto) * 100)}% del total` : null}
                            />
                        </div>
                        <div className="col-6 col-md-4 col-lg-3" style={{ minWidth: 0 }}>
                            <StatTile
                                label="Costo pasarela"
                                valor={fmtPeso(totales.pasarela)}
                                color="var(--ambar-600)"
                                icono="bi-credit-card"
                                sub={totalMonto > 0 ? `${Math.round((totales.pasarela / totalMonto) * 100)}% del total` : null}
                            />
                        </div>
                        <div className="col-6 col-md-4 col-lg-3" style={{ minWidth: 0 }}>
                            <StatTile
                                label="Urgencias cobradas"
                                valor={fmtPeso(totales.urgencia)}
                                color="var(--rojo-600)"
                                icono="bi-lightning-fill"
                            />
                        </div>
                        <div className="col-6 col-md-4 col-lg-3" style={{ minWidth: 0 }}>
                            <StatTile
                                label="Descuentos 1ra compra"
                                valor={fmtPeso(totales.descuento)}
                                color="var(--verde-esmeralda)"
                                icono="bi-gift-fill"
                                sub={`${totales.countConDescuento || 0} reserva${totales.countConDescuento !== 1 ? "s" : ""}`}
                            />
                        </div>
                        <div className="col-6 col-md-4 col-lg-3" style={{ minWidth: 0 }}>
                            <StatTile
                                label="Ticket promedio"
                                valor={fmtPeso(totales.ticketPromedio)}
                                color="var(--violeta-mapa)"
                                icono="bi-receipt"
                            />
                        </div>
                    </div>

                    {/* ── Gráfica de composición diaria ── */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-body p-4">
                            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                                <div>
                                    <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-bar-chart-fill me-2" style={{ color: "var(--azul-logo-claro)" }}></i>
                                        Composición de ingresos — últimos {dias} días
                                    </h6>
                                    <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                                        Cada barra muestra cómo se distribuye el dinero cobrado entre neto al vendedor, comisión, pasarela y urgencias.
                                    </p>
                                </div>
                                <div className="d-flex flex-wrap gap-3">
                                    {SERIES.map(s => (
                                        <div key={s.key} className="d-flex align-items-center gap-1">
                                            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }}></div>
                                            <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>{s.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {serie.some(d => d.montoTotal > 0)
                                ? <StackedBarChart serie={serie} dias={dias} />
                                : <p className="text-muted small mb-0 text-center py-4">Sin pagos en el período.</p>
                            }
                        </div>
                    </div>

                    {/* ── Ganancia neta real ── */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-body p-4">
                            <h6 className="fw-bold text-dark mb-1">
                                <i className="bi bi-wallet2 me-2 text-success"></i>
                                Ganancia neta real
                            </h6>
                            <p className="text-secondary mb-3" style={{ fontSize: "0.8rem" }}>
                                La comisión bruta no es lo que realmente nos queda — Wompi descuenta
                                retención en la fuente en cada cobro, y una comisión aparte por cada
                                desembolso enviado a un vendedor.
                            </p>
                            <div className="d-flex align-items-center gap-2 rounded-3 mb-3 p-2" style={{ background: "rgba(var(--verde-esmeralda-rgb), 0.12)" }}>
                                <i className="bi bi-gift-fill" style={{ color: "var(--verde-esmeralda)" }}></i>
                                <span style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)" }}>
                                    La comisión bruta de abajo ya tiene restados {fmtPeso(totales.descuento)} en descuentos
                                    de primera compra ({totales.countConDescuento || 0} reserva{totales.countConDescuento !== 1 ? "s" : ""}).
                                    {totales.descuento > 0 && ` Sin ese descuento habría sido ${fmtPeso(totales.comisionSinDescuento)}.`}
                                </span>
                            </div>
                            <div className="d-flex flex-column">
                                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid var(--color-track-bg)" }}>
                                    <span className="text-dark" style={{ fontSize: "0.88rem" }}>Comisión bruta</span>
                                    <span className="fw-semibold text-dark">{fmtPeso(totales.comision)}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid var(--color-track-bg)" }}>
                                    <span className="text-secondary" style={{ fontSize: "0.88rem" }}>
                                        − Retención en la fuente <span style={{ fontSize: "0.75rem" }}>(estimado, Wompi la descuenta del cobro)</span>
                                    </span>
                                    <span className="fw-semibold" style={{ color: "var(--rojo-600)" }}>− {fmtPeso(totales.retencionFuente)}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid var(--color-track-bg)" }}>
                                    <span className="text-secondary" style={{ fontSize: "0.88rem" }}>
                                        − Pagos a terceros <span style={{ fontSize: "0.75rem" }}>(comisión Wompi al desembolsar a vendedores)</span>
                                    </span>
                                    <span className="fw-semibold" style={{ color: "var(--rojo-600)" }}>− {fmtPeso(totales.pagosTerceros)}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center pt-3">
                                    <span className="fw-bold text-dark">Ganancia neta real</span>
                                    <span className="fw-bold" style={{ fontSize: "1.15rem", color: "var(--verde-bootstrap)" }}>{fmtPeso(totales.gananciaNeta)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4">
                        {/* ── Distribución porcentual ── */}
                        <div className="col-12 col-lg-6">
                            <div className="card border-0 shadow-sm rounded-4 h-100">
                                <div className="card-body p-4">
                                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-pie-chart-fill me-2" style={{ color: "var(--violeta-mapa)" }}></i>
                                        ¿A dónde va cada peso?
                                    </h6>
                                    <div className="d-flex flex-column gap-3">
                                        {[
                                            { key: "neto",     label: "Neto al vendedor",  color: "var(--verde-esmeralda)" },
                                            { key: "comision", label: "Comisión Zervicy",  color: "var(--azul-logo-claro)" },
                                            { key: "pasarela", label: "Costo pasarela",    color: "var(--ambar-600)" },
                                            { key: "urgencia", label: "Urgencias",         color: "var(--rojo-600)" },
                                        ].map(({ key, label, color }) => {
                                            const v   = totales[key] || 0;
                                            const pct = totalMonto > 0 ? Math.round((v / totalMonto) * 100) : 0;
                                            return <FilaBarra key={key} label={label} total={v} pct={pct} color={color} />;
                                        })}
                                    </div>
                                    {totalMonto > 0 && (
                                        <div className="mt-4 pt-3 border-top d-flex justify-content-between">
                                            <span className="text-muted small">Total recaudado</span>
                                            <span className="fw-bold text-dark">{fmtPeso(totalMonto)}</span>
                                        </div>
                                    )}
                                    {totalMonto > 0 && (
                                        <div className="mt-2 pt-2 d-flex justify-content-between align-items-start gap-2">
                                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>
                                                <i className="bi bi-gift-fill me-1" style={{ color: "var(--verde-esmeralda)" }}></i>
                                                Descuentos 1ra compra (no cobrados, no forman parte del 100% de arriba)
                                            </span>
                                            <span className="fw-semibold flex-shrink-0" style={{ fontSize: "0.78rem", color: "var(--verde-esmeralda)" }}>{fmtPeso(totales.descuento)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── Métodos de pago ── */}
                        <div className="col-12 col-lg-6">
                            <div className="card border-0 shadow-sm rounded-4 h-100">
                                <div className="card-body p-4">
                                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-credit-card-fill me-2" style={{ color: "var(--ambar-600)" }}></i>
                                        Métodos de pago
                                    </h6>
                                    {metodos.length === 0 ? (
                                        <p className="text-muted small mb-0">Sin datos.</p>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {metodos.map((m, i) => {
                                                const pct = totales.count > 0 ? Math.round((m.count / totales.count) * 100) : 0;
                                                const colors = ["var(--azul-logo-claro)", "var(--verde-esmeralda)", "var(--ambar-600)", "var(--violeta-mapa)", "var(--rojo-600)"];
                                                const color  = colors[i % colors.length];
                                                return (
                                                    <FilaBarra
                                                        key={m.tipo}
                                                        label={m.tipo || "Desconocido"}
                                                        total={m.monto}
                                                        pct={pct}
                                                        color={color}
                                                        sub={`${m.count} pago${m.count !== 1 ? "s" : ""}`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default ReportesPagos;
